/**
 * CardNotesButton — icon button with note count badge.
 *
 * Placed in the top-right corner of EnhancedMetricCard.
 * Shows the number of notes for this metric+period combo.
 * Clicking opens the CardNotesPopover.
 */

import { useMemo, useState } from 'react'
import { MessageSquare } from 'lucide-react'
import { useCardNotesStore, NOTES_ALLOWED_ROLES } from '@/stores/card-notes-store'
import { useAuth } from '@/hooks/useAuth'
import { CardNotesPopover } from './CardNotesPopover'

interface CardNotesButtonProps {
  metric: string
  metricLabel: string
  periodKey: string
}

export function CardNotesButton({ metric, metricLabel, periodKey }: CardNotesButtonProps) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const allNotes = useCardNotesStore((s) => s.notes)
  const notes = useMemo(
    () => allNotes.filter((n) => n.metric === metric && n.periodKey === periodKey),
    [allNotes, metric, periodKey]
  )

  // Only render for authorized roles
  const userRole = user?.role ?? ''
  if (!NOTES_ALLOWED_ROLES.includes(userRole)) return null

  const count = notes.length

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative p-1 rounded-md text-brand-gray hover:text-white hover:bg-brand-dark-lighter transition-colors"
        aria-label={`Notas de ${metricLabel} (${count})`}
      >
        <MessageSquare size={14} strokeWidth={1.5} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-brand-gray text-brand-dark text-[9px] font-bold px-0.5">
            {count}
          </span>
        )}
      </button>

      <CardNotesPopover
        open={open}
        onOpenChange={setOpen}
        metric={metric}
        metricLabel={metricLabel}
        periodKey={periodKey}
      />
    </>
  )
}
